import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateCustomerInput,
  CustomerFacets,
  CustomerResponse,
  ItemResponse,
  ListResponse,
  UpdateCustomerInput,
} from '@proerp/shared';
import { apiRequest, collectAllPages } from '@/lib/apiClient';

const CUSTOMERS_KEY = ['customers'] as const;

export interface CustomersQuery {
  q?: string;
  page?: number;
  pageSize?: number;
  sort?: string;
  type?: CustomerResponse['type'];
  status?: CustomerResponse['status'];
  country?: string;
  ownerLegalEntityId?: string;
  industry?: string;
}

function buildQueryString(query: CustomersQuery): string {
  const params = new URLSearchParams();
  params.set('page', String(query.page ?? 1));
  params.set('pageSize', String(query.pageSize ?? 50));
  if (query.sort) params.set('sort', query.sort);
  if (query.q) params.set('q', query.q);
  if (query.type) params.set('type', query.type);
  if (query.status) params.set('status', query.status);
  if (query.country) params.set('country', query.country);
  if (query.ownerLegalEntityId) params.set('ownerLegalEntityId', query.ownerLegalEntityId);
  if (query.industry) params.set('industry', query.industry);
  return params.toString();
}

/** Nicht-Hook-Fetch einer Kunden-Seite (für „Export alles" via collectAllPages). */
export function fetchCustomersPage(query: CustomersQuery): Promise<ListResponse<CustomerResponse>> {
  return apiRequest<ListResponse<CustomerResponse>>(`/v1/customers?${buildQueryString(query)}`);
}

export function useCustomers(query: CustomersQuery, options: { enabled?: boolean } = {}) {
  return useQuery({
    enabled: options.enabled ?? true,
    queryKey: [...CUSTOMERS_KEY, query],
    queryFn: () =>
      apiRequest<ListResponse<CustomerResponse>>(`/v1/customers?${buildQueryString(query)}`),
    placeholderData: keepPreviousData,
  });
}

/** Verfügbare Filterwerte (Länder & Branchen) für die Kundenliste-Dropdowns. */
export function useCustomerFacets() {
  return useQuery({
    queryKey: [...CUSTOMERS_KEY, 'facets'],
    queryFn: () => apiRequest<ItemResponse<CustomerFacets>>('/v1/customers/facets'),
    staleTime: 5 * 60_000,
  });
}

/** Lädt ALLE Adressen (für Auswertungen mit korrekter Verteilung). */
export function useAllCustomers() {
  return useQuery({
    queryKey: [...CUSTOMERS_KEY, 'all'],
    queryFn: () => collectAllPages((page, pageSize) => fetchCustomersPage({ page, pageSize })),
  });
}

export function useCustomer(id: string) {
  return useQuery({
    queryKey: [...CUSTOMERS_KEY, 'detail', id],
    queryFn: () => apiRequest<ItemResponse<CustomerResponse>>(`/v1/customers/${id}`),
    enabled: Boolean(id),
  });
}

export function useCreateCustomer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateCustomerInput) =>
      apiRequest<ItemResponse<CustomerResponse>>('/v1/customers', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: CUSTOMERS_KEY }),
  });
}

export function useUpdateCustomer(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateCustomerInput) =>
      apiRequest<ItemResponse<CustomerResponse>>(`/v1/customers/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: CUSTOMERS_KEY }),
  });
}

export function useDeleteCustomer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiRequest<{ data: { id: string } }>(`/v1/customers/${id}`, { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: CUSTOMERS_KEY }),
  });
}

export function useArchiveCustomer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiRequest<ItemResponse<CustomerResponse>>(`/v1/customers/${id}/archive`, { method: 'POST' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: CUSTOMERS_KEY }),
  });
}
